// Route metadata for every indexable marketing page.
// Consumed by the prerender script (head injection + sitemap) and the tests.

import { SITE_URL } from './constants'
import { STATES, stateBreadcrumbJsonLd } from './states'
import { GUIDES, guideJsonLd } from './guides'

export interface RouteMeta {
  path: string
  title: string
  description: string
  jsonLd?: string
  ogType?: 'website' | 'article'
}

const ORGANIZATION_JSONLD = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'ProcurAlerts',
      url: `${SITE_URL}/`,
      // No `logo`: production main ships no stable public logo image —
      // the property is omitted rather than pointing at a missing asset.
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: 'ProcurAlerts',
      url: `${SITE_URL}/`,
    },
  ],
})

// Hub page (/government-bids): honest WebPage + BreadcrumbList only.
// No GovernmentService or other types we cannot substantiate; the page is
// about the site section, referenced through the stable cross-host @ids.
const GOVERNMENT_BIDS_HUB_JSONLD = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Government Bids', item: `${SITE_URL}/government-bids` },
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/government-bids#webpage`,
      url: `${SITE_URL}/government-bids`,
      name: 'Government Bids Across New England | ProcurAlerts',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ],
})

export const ROUTE_META: RouteMeta[] = [
  {
    path: '/',
    title: 'ProcurAlerts — Government Bid Intelligence for New England Contractors',
    description:
      'ProcurAlerts monitors municipal and state procurement portals across all 6 New England states and delivers matched bids to your inbox daily.',
    jsonLd: ORGANIZATION_JSONLD,
  },
  {
    path: '/features',
    title: 'Features | ProcurAlerts',
    description:
      'Bid discovery across New England, trade- and county-matched alerts, deadline-sorted daily digests, watchlists, decision summaries, and bid package tools for contractors.',
  },
  {
    path: '/how-it-works',
    title: 'How It Works | ProcurAlerts',
    description:
      'Set your trade and territory, review matched public opportunities and deadlines, track them on your watchlist, and prepare a response with ProcurAlerts tools.',
  },
  {
    path: '/coverage',
    title: 'Coverage | ProcurAlerts',
    description:
      'ProcurAlerts covers public procurement opportunities across the six New England states: Massachusetts, Connecticut, Maine, New Hampshire, Rhode Island, and Vermont.',
  },
  {
    path: '/pricing',
    title: 'Pricing | ProcurAlerts',
    description:
      'Simple, transparent pricing for New England contractors. Pro and Team plans with a 14-day free trial, monthly or annual billing. Cancel anytime.',
  },
  {
    path: '/terms',
    title: 'Terms of Service | ProcurAlerts',
    description:
      'The terms that govern use of the ProcurAlerts subscription service for New England government bid intelligence.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | ProcurAlerts',
    description:
      'How ProcurAlerts collects, uses, and protects account and usage information for its New England bid intelligence service.',
  },
  {
    path: '/guides',
    title: 'Government Contracting Guides | ProcurAlerts',
    description: 'Practical guides to public procurement, government bidding, solicitation types, contractor certifications, prevailing wage, and bid submission.',
  },
  ...GUIDES.map((guide) => ({
    path: `/guides/${guide.slug}`,
    title: `${guide.title} | ProcurAlerts`,
    description: guide.description,
    jsonLd: guideJsonLd(guide),
    ogType: 'article' as const,
  })),
  // Regional hub above the six state acquisition pages.
  {
    path: '/government-bids',
    title: 'Government Bids Across New England | ProcurAlerts',
    description:
      'Browse public government bids from municipal, state, school, housing, and transit buyers across Massachusetts, Connecticut, Maine, New Hampshire, Rhode Island, and Vermont — no account required.',
    jsonLd: GOVERNMENT_BIDS_HUB_JSONLD,
  },
  // Six New England state acquisition pages (BreadcrumbList JSON-LD each).
  ...STATES.map((state) => ({
    path: `/government-bids/${state.slug}`,
    title: state.title,
    description: state.description,
    jsonLd: stateBreadcrumbJsonLd(state),
  })),
]

export function canonicalFor(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`
}
